import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import gsap from 'gsap';
import { revealOnScroll } from '../../utils/animations';
import MagneticButton from '../ui/MagneticButton';
import styles from './Contact.module.scss';

const contactSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  message: z.string().min(10, 'Message must be at least 10 characters')
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema)
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      revealOnScroll(containerRef.current);
    });
    return () => ctx.revert();
  }, []);

  const onSubmit = (data: ContactFormData) => {
    console.log('Form Submitted', data);
    alert('Thank you for your message! (Demo)');
  };

  return (
    <section className={styles.contactSection} id="contact">
      <div ref={containerRef} className={styles.container}>
        <div className={styles.content}>
          <h2 className={styles.sectionTitle}>
            Let's <span className={styles.highlight}>Collaborate</span>
          </h2>
          <p className={styles.description}>
            Ready to dominate search results and scale your business? Fill out the form, and let's discuss your growth strategy.
          </p>
          
          <div className={styles.info}>
            <div className={styles.infoBlock}>
              <strong>Location</strong>
              <p>Indore, India</p>
            </div>
            <div className={styles.infoBlock}>
              <strong>Phone & WhatsApp</strong>
              <p><a href="https://wa.me/916264454603" target="_blank" rel="noopener noreferrer" className={styles.contactLink}>+91 62644 54603</a></p>
            </div>
            <div className={styles.infoBlock}>
              <strong>Email</strong>
              <p><a href="mailto:rashdgtl@gmail.com" className={styles.contactLink}>rashdgtl@gmail.com</a></p>
            </div>
            <div className={styles.infoBlock}>
              <strong>Connect</strong>
              <div className={styles.socialLinks}>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">[ LinkedIn ]</a>
                <a href="https://www.instagram.com/rash_digital?igsh=MWZvc242dTlnZmU4bQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer">[ Instagram ]</a>
                <a href="https://www.facebook.com/share/19SpoJu4sL/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">[ Facebook ]</a>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.formContainer}>
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.inputGroup}>
              <input 
                type="text" 
                placeholder="Name" 
                {...register('name')} 
                className={errors.name ? styles.errorInput : ''}
              />
              {errors.name && <span className={styles.errorMessage}>{errors.name.message}</span>}
            </div>
            <div className={styles.inputGroup}>
              <input 
                type="email" 
                placeholder="Email Address" 
                {...register('email')}
                className={errors.email ? styles.errorInput : ''}
              />
              {errors.email && <span className={styles.errorMessage}>{errors.email.message}</span>}
            </div>
            <div className={styles.inputGroup}>
              <textarea 
                placeholder="Tell me about your goals..." 
                rows={5}
                {...register('message')}
                className={errors.message ? styles.errorInput : ''}
              />
              {errors.message && <span className={styles.errorMessage}>{errors.message.message}</span>}
            </div>
            <div className={styles.submitWrapper}>
              <MagneticButton className={styles.submitBtn}>
                Send Message
              </MagneticButton>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
